import { useMemo, useRef, useState } from 'react';
import { Button, Form, Mentions, message } from 'antd';
import TimelineGroups from './timeline';
import { getMentionUsers } from '../../services/dropdownApi';

/* Turn one API user record into a { value, label } mention option.
 * value is the inserted token (kept space-free so the @mention renders as a
 * single tag); label is the friendlier name shown in the dropdown. */
function normalizeMentionUser(item) {
  const userName = item?.user_name ?? item?.userName ?? item?.username;
  const first = item?.first_name ?? item?.firstName ?? '';
  const last = item?.last_name ?? item?.lastName ?? '';
  const fullName = `${first} ${last}`.trim();
  const email = item?.email ?? '';
  const value = userName ?? fullName.replace(/\s+/g, '') ?? email
    ?? String(item?.user_id ?? item?.id ?? '');
  const label = [userName, fullName, email].filter(Boolean).join(' - ') || String(value);
  return { value: String(value), label };
}

const sampleNotes = [
  {
    id: 'n1',
    createdAt: new Date().toISOString(),
    author: 'You',
    notes: 'Shortlisted 3 candidates, sharing profiles with @Hiring Manager today.',
  },
];

/* Render note text, turning @mentions into inline brand-tinted chips. A borderless
 * inline chip sits on the text baseline; an antd Tag carries its own margins and
 * border and breaks the line rhythm of a sentence it is embedded in. */
function renderNoteText(text = '') {
  return text.split(/(\s+)/).map((chunk, index) => {
    if (chunk.startsWith('@') && chunk.length > 1) {
      return (
        <span className="znx-an-mention" key={index}>
          {chunk}
        </span>
      );
    }
    return <span key={index}>{chunk}</span>;
  });
}

export default function NotesTab({ notes, users = [], onAddNote }) {
  const [form] = Form.useForm();
  const [localNotes, setLocalNotes] = useState(() =>
    Array.isArray(notes) ? notes : sampleNotes,
  );
  const [posting, setPosting] = useState(false);
  // Live mention users fetched from the API when the user types after '@'.
  // null = not searched yet → fall back to any users passed in via props.
  const [mentionUsers, setMentionUsers] = useState(null);
  const [mentionLoading, setMentionLoading] = useState(false);
  const searchTimer = useRef(null);

  const rows = Array.isArray(notes) ? notes : localNotes;

  const fallbackOptions = useMemo(
    () => users.map((user) => ({ value: user.display ?? user.value, label: user.display ?? user.label })),
    [users],
  );
  const mentionOptions = mentionUsers ?? fallbackOptions;

  // Debounced @-mention search → calls the shared getMentionUsers API and
  // lists matching users in the Mentions dropdown.
  const handleMentionSearch = (search) => {
    clearTimeout(searchTimer.current);
    setMentionLoading(true);
    searchTimer.current = setTimeout(() => {
      getMentionUsers(search)
        .then((data) => {
          const raw = Array.isArray(data) ? data : (data?.items ?? data?.list ?? data?.data ?? []);
          setMentionUsers(raw.map(normalizeMentionUser));
        })
        .catch(() => setMentionUsers([]))
        .finally(() => setMentionLoading(false));
    }, 300);
  };

  const handlePost = async ({ note }) => {
    const text = (note ?? '').trim();
    if (!text) return;

    setPosting(true);
    try {
      if (onAddNote) {
        await onAddNote(text);
      } else {
        setLocalNotes((prev) => [
          { id: `local-${Date.now()}`, createdAt: new Date().toISOString(), author: 'You', notes: text },
          ...prev,
        ]);
      }
      form.resetFields();
      message.success('Notes added successfully');
    } catch {
      message.error('Something went wrong, please try again.');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="znx-an-body">
      <Form form={form} onFinish={handlePost} className="znx-an-composer">
        <Form.Item
          name="note"
          rules={[{ required: true, message: 'Notes is required' }]}
          style={{ marginBottom: 0 }}
        >
          <Mentions
            rows={3}
            prefix="@"
            options={mentionOptions}
            loading={mentionLoading}
            filterOption={false}
            onSearch={handleMentionSearch}
            placeholder="Add your notes on the job, use '@' to mention"
          />
        </Form.Item>
        <div className="znx-an-composer-actions">
          <Button type="primary" size="small" htmlType="submit" loading={posting}>
            Post
          </Button>
        </div>
      </Form>

      <div className="znx-an-scroll">
        <TimelineGroups
          rows={rows}
          getTime={(row) => row.createdAt ?? row.activityTime}
          getKey={(row, index) => row.id ?? row._id ?? index}
          getAuthor={(row) => row.author}
          emptyText="No notes here."
          renderBody={(row) => (
            <div className="znx-an-comment">{renderNoteText(row.notes)}</div>
          )}
        />
      </div>
    </div>
  );
}
